/* ============================================================
 * <complete-the-set> — necklace + matching bracelet bundle.
 *
 * Toggle on + pick a size, then the main "Add to bag" adds BOTH
 * the necklace and the bracelet in one action, then hands the
 * rendered cart sections to the theme's own cart drawer
 * (renderContents) — the same path a normal add uses — so the
 * item list, empty-state, and drawer all update correctly.
 *
 * - No default size; if the toggle is on with no size chosen,
 *   clicking Add to bag nudges the size pills instead of adding.
 * ============================================================ */
class CompleteTheSet extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-cts-toggle]');
    this.sizesWrap = this.querySelector('[data-cts-sizes]');
    this.sizes = Array.from(this.querySelectorAll('[data-cts-size]'));
    this.selectedVariant = null;
    // Single-variant bracelet (no size pills): its lone variant id, auto-selected
    // when the toggle is on so Add to bag adds it without a size choice.
    this.singleVariant = this.dataset.ctsSingle || null;

    this.form = document.getElementById(this.dataset.formId);
    this.submitBtn = this.form ? this.form.querySelector('[name="add"]') : null;

    if (this.toggle) this.toggle.addEventListener('change', () => this.onToggle());
    this.sizes.forEach((el) => el.addEventListener('click', () => this.selectSize(el)));

    if (this.form) {
      // Capture so we intercept before the theme's own submit handler.
      this.form.addEventListener('submit', (event) => this.onFormSubmit(event), true);
    }
  }

  onToggle() {
    const on = this.toggle.checked;
    // Drive the box's active state + the animated size reveal via classes (CSS
    // handles the transitions); no more display:none toggling.
    this.classList.toggle('is-active', on);
    if (this.sizesWrap) this.sizesWrap.classList.toggle('is-open', on);
    if (on) {
      if (this.singleVariant) this.selectedVariant = this.singleVariant;
    } else {
      this.clearSize();
      this.classList.remove('cts--prompt');
    }
  }

  selectSize(el) {
    if (el.hasAttribute('disabled')) return;
    this.sizes.forEach((s) => s.classList.remove('is-selected'));
    el.classList.add('is-selected');
    this.selectedVariant = el.getAttribute('data-cts-variant');
    this.classList.remove('cts--prompt');
  }

  clearSize() {
    this.sizes.forEach((s) => s.classList.remove('is-selected'));
    this.selectedVariant = null;
  }

  // Bundle on but no size chosen: scroll to + briefly nudge the size pills so the
  // shopper sees what to pick — never a silently dead "Add to bag" button.
  promptSize() {
    if (!this.sizesWrap) return;
    try {
      this.sizesWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (e) {
      this.sizesWrap.scrollIntoView();
    }
    this.classList.add('cts--prompt');
    clearTimeout(this._promptTimer);
    this._promptTimer = setTimeout(() => this.classList.remove('cts--prompt'), 1000);
  }

  // Mirror the theme's add-to-bag loading state (spinner + disabled) so the bundle
  // add gives the same feedback as a normal add.
  setLoading(on) {
    if (!this.submitBtn) return;
    const spinner = this.submitBtn.querySelector('.loading-overlay__spinner');
    if (on) {
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');
    } else {
      this.submitBtn.classList.remove('loading');
      this.submitBtn.removeAttribute('aria-disabled');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  onFormSubmit(event) {
    // Bundle off → let the normal single-item add proceed.
    if (!this.toggle || !this.toggle.checked) return;

    // Bundle on but no size chosen yet. If a selectable size exists, block the add
    // and guide the shopper to pick one (so the click always does something). If
    // every partner size is sold out, fall through to a normal add of THIS item.
    if (!this.selectedVariant) {
      const hasSelectableSize = this.sizes.some((s) => !s.hasAttribute('disabled'));
      if (hasSelectableSize) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.promptSize();
      }
      return;
    }

    // Bundle on + size chosen → add THIS item + the partner together.
    event.preventDefault();
    event.stopImmediatePropagation();

    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    // The theme's cart element renders the drawer; ask it which sections to fetch
    // so /cart/add.js returns the rendered HTML and renderContents() can swap them.
    const cart =
      document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const sectionIds =
      cart && typeof cart.getSectionsToRender === 'function'
        ? cart.getSectionsToRender().map((s) => s.id)
        : [];
    const formData = new FormData(this.form);
    const mainBody = new URLSearchParams(formData).toString();
    const mainVariantId = formData.get('id');

    this.setLoading(true); // spinner on Add to bag, like a normal add

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: mainBody,
    })
      .then((r) => {
        if (!r.ok) throw new Error('main add failed');
        return r.json();
      })
      .then(() =>
        // Add the partner LAST, with sections, so the response carries the final
        // cart's rendered drawer (both items present).
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: parseInt(this.selectedVariant, 10),
                quantity: 1,
                properties: { _bundle: 'Complete the Set', _added_with_variant: mainVariantId },
              },
            ],
            sections: sectionIds,
            sections_url: window.location.pathname,
          }),
        })
      )
      .then((r) => {
        if (!r.ok) throw new Error('partner add failed');
        return r.json();
      })
      .then((response) => {
        // Hand the rendered sections to the theme's own drawer — this re-renders
        // the item list + cart icon and opens the drawer.
        if (cart && typeof cart.renderContents === 'function' && response && response.sections) {
          cart.renderContents(response);
          // The theme's renderContents clears `is-empty` from the wrong element, so
          // (exactly like product-form.js) clear it on the cart here — otherwise the
          // items stay hidden when adding from a previously-empty cart.
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          this.setLoading(false);
        } else {
          window.location.href = cartUrl; // no drawer available → full cart page
        }
      })
      .catch((error) => {
        console.error('[Complete the Set] add failed:', error);
        this.setLoading(false);
        // The items may already be in the cart; go to the cart page rather than
        // re-submitting the form (which could double-add).
        window.location.href = cartUrl;
      });
  }
}

if (!customElements.get('complete-the-set')) {
  customElements.define('complete-the-set', CompleteTheSet);
}
